/**
 * Job options for the trimmer and the cutter, built from xconfig's per-output defaults.
 *
 * Until 2026-10-11 both components sent a codec from a hand-kept map (utils/media/codec-resolver.ts) that disagreed with
 * xconfig for ~20 outputs (TS/MTS/M2TS sent MPEG2 + MP2 where xconfig says H264 + AAC, OGV sent H264, WEBA/AU/AIFC sent
 * AAC, ...; manual-qa/seo-sot-20261010/SOURCE-OF-TRUTH.md R12/R13, plan P-A1). Now the job is xconfig's default summary
 * for the chosen output, resolved by the same engine every converter page uses (ConvertOptionsContextV2), plus the
 * process. av-job-options.xconfig.drift.spec.ts pins it. Until 2026-10-11 (round 3) the web also overrode three trim
 * defaults (size 100 %, no auto-scale, original resolution; audio: File size 100 %) to keep the source quality; those
 * now live in xconfig's TRIM trees (src/domain/modifiers/by-process-type/trim-*-options.modifiers.ts, owner decision
 * "for trim, keep full quality"), so the public API's sparse trim gets them too and the web adds nothing.
 *
 * Which tree: the TRIM hubs `video-trimmer` / `audio-trimmer` (public/convert-options-v2/<slug>.json). Their toExtension
 * group offers every trim output xconfig has, and xconfig's codec default is a function of toExtension alone
 * (src/data/media/video/video-codec.options.json and audio-codec.options.ts `restoreStateContexts`), so the hub resolves
 * the same codec as the single-output trim-<ext> page. xconfig has no CUT option tree (feature-metadata cut-* has
 * optionPath cut-<ext> but no convert-options file); the cutter reads the TRIM hub and sends process CUT.
 *
 * Outputs xconfig does not offer -- reachable only through "Same As Input" for an input-only format such as 3GPP, CAVS,
 * DV, DVR, CAF, DSS or VOC (M4R left this list on 2026-10-11, when xconfig gained it as an audio output) -- keep the
 * tree's non-codec defaults and send NO codec, so ironman picks its own
 * container default (FFmpegCodecRegistry / ffmpeg's muxer default). A web table for them would be a second source.
 */
import { ConvertOptionsContextV2, ConvertOptionsSummary } from '../convert-options-v2/convert-options.context';

/** xconfig option-tree slugs the trimmer and cutter resolve their defaults from. */
export const AV_JOB_TREE = { video: 'video-trimmer', audio: 'audio-trimmer' } as const;

const CODEC_KEYS = ['videoCodec', 'audioCodec'];

export interface XconfigJobDefaults {
    summary: ConvertOptionsSummary;
    /** False when xconfig has no such output: `summary` then carries no codec (ironman's container default applies). */
    offered: boolean;
}

/** xconfig's default summary of `tree` for output `toExtension` (upper case, no dot). */
export function xconfigJobDefaults(tree: unknown, toExtension: string): XconfigJobDefaults {
    const ctx = ConvertOptionsContextV2.newContext(tree);
    ctx.applySummary({ toExtension });
    const summary = ctx.getSummary();
    if (summary.toExtension === toExtension) {
        return { summary, offered: true };
    }
    const base = ConvertOptionsContextV2.newContext(tree).getSummary();
    CODEC_KEYS.forEach(key => delete base[key]);
    return { summary: { ...base, toExtension }, offered: false };
}

/**
 * The option bag of a trim / cut job (the caller adds outputTrim / outputCut): xconfig's TRIM default for the output,
 * unchanged, with the process. `isAudio` is kept for the callers' shape; the tree already decides the media.
 */
export function buildAvJobOptions(
    tree: unknown,
    job: { process: 'TRIM' | 'CUT'; isAudio: boolean; toExtension: string },
): Record<string, any> {
    const { summary } = xconfigJobDefaults(tree, job.toExtension);
    return { ...summary, process: job.process };
}
